import React, { createContext, useContext, useMemo } from 'react'
import { useWallet } from './WalletContext'

const AccessTierContext = createContext()

export function useAccessTier() {
  return useContext(AccessTierContext)
}

// Access tier thresholds
const TIERS = {
  BRONZE: {
    name: 'Bronze',
    min: 0,
    max: 1000,
    color: 'from-orange-600 to-orange-400',
    bgColor: 'bg-orange-500/10',
    borderColor: 'border-orange-400/30',
    icon: '🥉',
    benefits: [
      'Access to basic robot information',
      'View public statistics',
      'Community forum access'
    ]
  },
  SILVER: {
    name: 'Silver',
    min: 1000,
    max: 10000,
    color: 'from-gray-400 to-gray-200',
    bgColor: 'bg-gray-400/10',
    borderColor: 'border-gray-300/30',
    icon: '🥈',
    benefits: [
      'All Bronze benefits',
      'Detailed robot analytics',
      'Advanced filtering options',
      'Price history & trends',
      '5% discount on bookings'
    ]
  },
  GOLD: {
    name: 'Gold',
    min: 10000,
    max: Infinity,
    color: 'from-amber-500 via-yellow-400 to-amber-600',
    bgColor: 'bg-gradient-to-br from-yellow-500/10 to-amber-600/10',
    borderColor: 'border-yellow-500/40',
    icon: '🏆',
    benefits: [
      'All Silver benefits',
      'Exclusive premium robots',
      'Priority booking',
      'Real-time robot monitoring',
      '15% discount on all bookings',
      'Early access to new features',
      'Dedicated support channel'
    ]
  }
}

export function AccessTierProvider({ children }) {
  const { tokenBalance, isConnected } = useWallet()

  // Calculate user's tier based on token balance
  const userTier = useMemo(() => {
    if (!isConnected) {
      return null
    }

    // Parse balance (remove commas and convert to number)
    const balance = parseFloat(tokenBalance.replace(/,/g, '')) || 0

    if (balance >= TIERS.GOLD.min) {
      return TIERS.GOLD
    } else if (balance >= TIERS.SILVER.min) {
      return TIERS.SILVER
    } else {
      return TIERS.BRONZE
    }
  }, [tokenBalance, isConnected])

  // Check if user has access to a specific tier
  const hasAccess = (requiredTier) => {
    if (!userTier) return false
    
    const tierOrder = { BRONZE: 1, SILVER: 2, GOLD: 3 }
    const userTierLevel = tierOrder[userTier.name.toUpperCase()]
    const requiredTierLevel = tierOrder[requiredTier.toUpperCase()]
    
    return userTierLevel >= requiredTierLevel
  }

  // Get tokens needed to reach next tier
  const tokensToNextTier = useMemo(() => {
    if (!userTier || userTier.name === 'Gold') return 0
    
    const balance = parseFloat(tokenBalance.replace(/,/g, '')) || 0
    
    if (userTier.name === 'Bronze') {
      return TIERS.SILVER.min - balance
    } else if (userTier.name === 'Silver') {
      return TIERS.GOLD.min - balance
    }
    
    return 0
  }, [userTier, tokenBalance])

  const value = {
    userTier,
    tiers: TIERS,
    hasAccess,
    tokensToNextTier,
    isConnected
  }

  return (
    <AccessTierContext.Provider value={value}>
      {children}
    </AccessTierContext.Provider>
  )
}
